<script setup lang="ts">
    import { watch, ref, computed } from 'vue'
    import { getFileIcon, type Media, MediaType } from 'models'
    import { useMediaStore } from 'composables'
    import PkLazyImg from './PkLazyImg.vue'

    const props = defineProps<{
        media: Media
        alt?: string
        width?: string | number
        height?: string | number
    }>()

    const { getDownloadHandle } = useMediaStore()

    const src = ref<string>()
    const isLoading = ref(false)
    watch(
        () => props.media,
        async (newValue) => {
            src.value = undefined
            if (newValue) {
                isLoading.value = true
                try {
                    src.value = (
                        await getDownloadHandle(newValue.src)
                    )?.downloadHandle.previewSasuri
                } catch {
                    src.value = undefined
                } finally {
                    isLoading.value = false
                }
            }
        },
        { immediate: true },
    )

    const iconName = computed(() => {
        switch (props.media.src.type) {
            case MediaType.picture:
                return 'ri:file-image-line'
            case MediaType.video:
                return 'ri:file-video-line'
            case MediaType.audio:
                return 'ri:file-music-line'
            default:
                return getFileIcon(props.media.src.extension)
        }
    })

    const onImageError = () => {
        src.value = undefined
    }
</script>

<template>
    <picture class="relative" :class="{ 'vv-skeleton': isLoading }">
        <div
            v-if="!src"
            class="aspect-square flex items-center justify-center text-18"
            :style="{
                width: `${width}px`,
                height: `${height}px`,
            }">
            <VvIcon :name="iconName" />
        </div>
        <PkLazyImg
            v-else
            :src
            :alt
            :width
            :height
            class="block w-full rounded-md"
            @error="onImageError" />
        <div
            v-if="isLoading"
            class="absolute inset-0 vv-skeleton__item z-1 h-auto"></div>
    </picture>
</template>
